import { useCallback, useEffect, useState } from "react";
import { Badge, Button, Form, Modal, Table } from "react-bootstrap";
import InvoiceDocument from "./InvoiceDocument.jsx";
import TabHeader from "./TabHeader.jsx";
import {
  listProgressClaims,
  getProgressClaim,
  createProgressClaim,
  updateProgressLine,
  progressSetAll100,
  progressRefresh,
  progressFinish,
  deleteProgressClaim,
} from "../../services/apiClient.js";

// Progress Claims — %-complete billing cycles against the quote. Pick a cycle, set
// each line's New %, and the engine fills Previous / This cycle / Total claimed.
// Finishing a cycle locks it; the next cycle carries the % forward.

const nzd = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" });
const money = (v) => nzd.format(Number(v || 0));
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
function lineMoney(amount, existing, newPct) {
  const a = Number(amount) || 0;
  const prev = round2((a * existing) / 100);
  const total = round2((a * newPct) / 100);
  return { previous_amount_claimed: prev, total_amount_claimed: total, amount_claimed: round2(total - prev) };
}

function ClaimProgressClaimsTab({ claimId, onChanged }) {
  const [claims, setClaims] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [showInvoice, setShowInvoice] = useState(false);

  function openInvoice() {
    document.body.classList.add("print-modal");
    setShowInvoice(true);
  }
  function closeInvoice() {
    document.body.classList.remove("print-modal");
    setShowInvoice(false);
  }
  useEffect(() => () => document.body.classList.remove("print-modal"), []);

  const loadList = useCallback(
    async (selectId) => {
      try {
        const list = await listProgressClaims(claimId);
        setClaims(list);
        setSelectedId((cur) => {
          const want = selectId ?? cur;
          if (want && list.some((c) => Number(c.id) === Number(want))) return want;
          return list.length ? list[list.length - 1].id : null;
        });
      } catch {
        setError("Unable to load progress claims.");
      }
    },
    [claimId]
  );
  const loadDetail = useCallback(async () => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    try {
      setDetail(await getProgressClaim(claimId, selectedId));
    } catch {
      setDetail(null);
    }
  }, [claimId, selectedId]);

  useEffect(() => {
    loadList();
  }, [loadList]);
  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const hasOpen = claims.some((c) => !c.complete);
  const open = detail && !detail.complete;
  const isLatest = detail && claims.length && Number(claims[claims.length - 1].id) === Number(detail.id);

  async function create() {
    setError("");
    setNotice("");
    try {
      const c = await createProgressClaim(claimId);
      await loadList(c.id);
      setDetail(c);
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to create progress claim.");
    }
  }

  function onPctChange(line, value) {
    const newPct = Math.max(0, Math.min(100, Math.round(Number(value) || 0)));
    setDetail((d) => ({
      ...d,
      lines: d.lines.map((l) =>
        l.id === line.id ? { ...l, new_percentage: newPct, ...lineMoney(l.amount, l.existing_percentage, newPct) } : l
      ),
    }));
  }
  async function commitPct(line) {
    setError("");
    try {
      setDetail(await updateProgressLine(claimId, detail.id, line.id, { new_percentage: line.new_percentage }));
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to update line.");
      loadDetail();
    }
  }
  async function toggleHide(line) {
    setError("");
    try {
      setDetail(await updateProgressLine(claimId, detail.id, line.id, { hide_in_invoice: !line.hide_in_invoice }));
    } catch (err) {
      setError(err.response?.data?.error || "Unable to update line.");
    }
  }
  async function run(fn, msg) {
    setError("");
    setNotice("");
    try {
      setDetail(await fn());
      await loadList();
      if (msg) setNotice(msg);
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || "Action failed.");
    }
  }
  async function del() {
    if (!window.confirm(`Delete progress claim #${detail.claim_no}?`)) return;
    setError("");
    try {
      await deleteProgressClaim(claimId, detail.id);
      setSelectedId(null);
      await loadList();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to delete.");
    }
  }

  const totals = (detail?.lines || []).reduce(
    (a, l) => {
      a.amount += Number(l.amount || 0);
      a.prev += Number(l.previous_amount_claimed || 0);
      a.this += Number(l.amount_claimed || 0);
      a.total += Number(l.total_amount_claimed || 0);
      return a;
    },
    { amount: 0, prev: 0, this: 0, total: 0 }
  );

  return (
    <>
      <TabHeader
        title="Progress Claims"
        help="progressClaims"
        actions={
          <Button size="sm" onClick={create} disabled={hasOpen} data-hint={hasOpen ? "Finish the open claim first" : undefined}>
            + New Claim Cycle
          </Button>
        }
      />

      {claims.length ? (
        <div className="d-flex flex-wrap gap-2 mb-3">
          {claims.map((c) => (
            <Button
              key={c.id}
              size="sm"
              variant={Number(c.id) === Number(selectedId) ? "primary" : "outline-primary"}
              onClick={() => setSelectedId(c.id)}
            >
              Claim {c.claim_no} {c.complete ? "✓" : "•"}
            </Button>
          ))}
        </div>
      ) : null}

      {notice ? <div className="alert alert-info">{notice}</div> : null}
      {error ? <div className="alert alert-danger">{error}</div> : null}

      {!detail ? (
        <p className="text-muted">
          {claims.length ? "Select a claim cycle." : "No progress claims yet. Create the first cycle from the quote."}
        </p>
      ) : (
        <>
          <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
            <span>
              Claim #{detail.claim_no} —{" "}
              {detail.complete ? <Badge bg="success">Complete</Badge> : <Badge bg="warning" text="dark">Creating</Badge>}
            </span>
            <div className="ms-auto d-flex flex-wrap gap-2">
              {detail.lines.length ? (
                <Button size="sm" variant="outline-success" onClick={openInvoice}>Invoice ▸</Button>
              ) : null}
              {open ? (
                <>
                  <Button size="sm" variant="outline-secondary" onClick={() => run(() => progressRefresh(claimId, detail.id))}>
                    Refresh amounts
                  </Button>
                  <Button size="sm" variant="outline-secondary" onClick={() => run(() => progressSetAll100(claimId, detail.id))}>
                    Set remaining to 100%
                  </Button>
                  <Button size="sm" onClick={() => run(() => progressFinish(claimId, detail.id), "Claim finished and locked.")}>
                    Finish claim
                  </Button>
                </>
              ) : null}
              {isLatest ? (
                <Button size="sm" variant="outline-danger" onClick={del}>Delete claim</Button>
              ) : null}
            </div>
          </div>

          <Table bordered hover responsive size="sm" className="mb-0 align-middle">
            <thead className="table-surface">
              <tr>
                <th>Description</th>
                <th>Cost Type</th>
                <th className="text-end">Amount</th>
                <th className="text-end">Prev %</th>
                <th className="text-end" style={{ width: 90 }}>New %</th>
                <th className="text-end">Previous</th>
                <th className="text-end">This Claim</th>
                <th className="text-end">Total</th>
                <th className="text-center" style={{ width: 60 }}>Hide</th>
              </tr>
            </thead>
            <tbody>
              {detail.lines.map((l) => (
                <tr key={l.id} className={l.hide_in_invoice ? "text-muted" : undefined}>
                  <td style={{ whiteSpace: "pre-wrap", minWidth: 180 }}>{l.description || <span className="text-muted">—</span>}</td>
                  <td className="small">{l.cost_type}</td>
                  <td className="text-end text-nowrap">{money(l.amount)}</td>
                  <td className="text-end">{l.existing_percentage}%</td>
                  <td className="text-end">
                    {open ? (
                      <Form.Control
                        size="sm"
                        type="number"
                        min={0}
                        max={100}
                        value={l.new_percentage}
                        onChange={(e) => onPctChange(l, e.target.value)}
                        onBlur={() => commitPct(l)}
                        className="text-end"
                        style={{ width: 70 }}
                      />
                    ) : (
                      `${l.new_percentage}%`
                    )}
                  </td>
                  <td className="text-end text-nowrap">{money(l.previous_amount_claimed)}</td>
                  <td className="text-end text-nowrap fw-semibold">{money(l.amount_claimed)}</td>
                  <td className="text-end text-nowrap">{money(l.total_amount_claimed)}</td>
                  <td className="text-center">
                    <Form.Check type="checkbox" checked={l.hide_in_invoice} disabled={!open} onChange={() => toggleHide(l)} />
                  </td>
                </tr>
              ))}
              {detail.lines.length === 0 ? (
                <tr><td colSpan={9} className="text-center text-muted">No lines.</td></tr>
              ) : null}
            </tbody>
            {detail.lines.length ? (
              <tfoot>
                <tr className="table-surface fw-semibold">
                  <td colSpan={2} className="text-end">Totals</td>
                  <td className="text-end text-nowrap">{money(totals.amount)}</td>
                  <td colSpan={2} />
                  <td className="text-end text-nowrap">{money(totals.prev)}</td>
                  <td className="text-end text-nowrap">{money(totals.this)}</td>
                  <td className="text-end text-nowrap">{money(totals.total)}</td>
                  <td />
                </tr>
              </tfoot>
            ) : null}
          </Table>
        </>
      )}

      <Modal show={showInvoice} onHide={closeInvoice} size="lg" centered>
        <Modal.Header closeButton className="no-print">
          <Modal.Title>Invoice — Progress Claim {detail ? `#${detail.claim_no}` : ""}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {showInvoice && detail ? <InvoiceDocument claimId={claimId} pcId={detail.id} /> : null}
        </Modal.Body>
        <Modal.Footer className="no-print">
          <Button variant="secondary" onClick={closeInvoice}>Close</Button>
          <Button onClick={() => window.print()}>Print</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default ClaimProgressClaimsTab;
